/**
 * API Client pour l'e-commerce
 */

import { api } from '../services/api';
import { logger } from '../utils/logger';
import type {
  Customer,
  CustomerAddress,
  Product,
  Category,
  Order,
  CreateOrderDto,
  Wallet,
  WalletTransaction,
  LiveSession,
  VendorAvailability,
  ProductFilters,
  OrderRefund,
} from '../types/ecommerce.types';

// ============================================================================
// AUTHENTICATION (Client e-commerce)
// ============================================================================

export interface CustomerLoginDto {
  email: string;
  password: string;
}

export interface CustomerRegisterDto {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  marketing_consent?: boolean;
}

export interface CustomerAuthResponse {
  access_token: string;
  customer: Customer;
}

export const customerAuthApi = {
  login: (dto: CustomerLoginDto): Promise<CustomerAuthResponse> =>
    api.post('/customers/auth/login', dto),

  register: (dto: CustomerRegisterDto): Promise<CustomerAuthResponse> =>
    api.post('/customers/auth/register', dto),

  getProfile: (): Promise<Customer> =>
    api.get('/customers/me'),

  updateProfile: (_customerId: string, data: Partial<Customer>): Promise<Customer> =>
    api.patch('/customers/me', data),

  /** Preferences du client — aujourd'hui : la liste de ses favoris. */
  updatePreferences: (data: { favorite_products?: string[] }): Promise<unknown> =>
    api.patch('/customers/me/preferences', data),

  forgotPassword: (email: string): Promise<{ message: string }> =>
    api.post('/customers/auth/forgot-password', { email }),

  resetPassword: (token: string, new_password: string): Promise<{ message: string }> =>
    api.post('/customers/auth/reset-password', { token, new_password }),
};

// ============================================================================
// PRODUCTS & CATEGORIES
// ============================================================================

export const ecommerceProductsApi = {
  getProducts: async (filters?: ProductFilters): Promise<Product[]> => {
    // Pour l'e-commerce, toujours filtrer les produits en rupture de stock
    // limit élevé : 1000 tronquait le catalogue (produits triés en fin É…/Œ… perdus)
    // is_deposit=false : une consigne n'est pas un article vendable — elle est
    // ajoutée automatiquement avec le contenant (deposit_product_id) et remboursée
    // au retour. Elle ne doit jamais apparaître au catalogue client. (Le POS, lui,
    // a besoin des fiches consigne dans sa liste — d'où le filtre ici et non par
    // défaut côté serveur, cf. POSContent.tsx.)
    // active=true : une fiche inactive est une fiche pas encore validée (reçue,
    // pas mise en rayon, prix non arbitré). Le serveur ne l'applique pas par
    // défaut — le POS a besoin des inactifs — c'est donc au client de le dire.
    const params = { ...filters, in_stock_only: true, is_deposit: false, active: true, limit: 100000 };
    const response = await api.get('/products', { params });
    // L'API retourne { data: [...], pagination: {...} }
    return response.data || response;
  },

  getProductById: (productId: string): Promise<Product> =>
    api.get(`/products/${productId}`),

  searchProducts: async (query: string): Promise<Product[]> => {
    // Mêmes exclusions que getProducts : ni consigne, ni fiche non validée.
    const response = await api.get('/products', { params: { search: query, is_deposit: false, active: true } });
    // L'API retourne { data: [...], pagination: {...} }
    return response.data || response;
  },

  getCategories: (): Promise<Category[]> =>
    api.get('/categories'),

  getCategoryById: (categoryId: string): Promise<Category> =>
    api.get(`/categories/${categoryId}`),
};

// ============================================================================
// ORDERS
// ============================================================================

export interface AvailabilityIssue {
  product_id: string;
  product_name: string;
  requested: number;
  available: number;
  is_weighted: boolean;
  unit: string;
  reason: 'INSUFFICIENT_STOCK' | 'UNKNOWN_PRODUCT';
}

export const ecommerceOrdersApi = {
  /**
   * Le panier est-il encore servable ? À appeler AVANT le paiement : le
   * catalogue n'affiche plus les quantités, c'est donc ici que le client
   * apprend qu'un article n'est plus disponible — et lesquels, tous d'un coup.
   */
  checkAvailability: (
    lines: Array<{
      product_id: string;
      product_name?: string;
      quantity: number;
      quantity_unit?: string;
      is_weighted?: boolean;
    }>,
  ): Promise<{ ok: boolean; issues: AvailabilityIssue[] }> =>
    api.post('/orders/check-availability', { lines }),

  createOrder: (dto: CreateOrderDto): Promise<Order> =>
    api.post('/orders', dto),

  /**
   * Mettre le panier de côté AVANT de partir recharger le compte.
   *
   * Le panier vit dans le stockage du navigateur : quand Stripe renvoie le
   * client dans un autre onglet (navigateur interne de Facebook, 05/09/2026),
   * il ne le retrouve pas et l'argent est encaissé sans commande. Déposé ici,
   * c'est le serveur qui crée la commande au moment du crédit.
   */
  createDraft: (dto: CreateOrderDto): Promise<{ draft_id: string; order_number: string; expected_total_cents: number }> =>
    api.post('/orders/draft', dto),

  getDraft: (
    draftId: string,
  ): Promise<{
    id: string;
    status: 'pending' | 'processing' | 'consumed' | 'empty' | 'failed';
    order_id: string | null;
    adjustments: { retirees: Array<{ produit: string }>; reduites: Array<{ produit: string; demande: number; servi: number }> } | null;
    failure_reason: string | null;
  }> => api.get(`/orders/draft/${draftId}`),

  getCustomerOrders: (filters?: { status?: string }): Promise<Order[]> =>
    api.get('/orders', { params: filters }),

  getOrderById: (orderId: string): Promise<Order> =>
    api.get(`/orders/${orderId}`),

  cancelOrder: (orderId: string, reason?: string): Promise<Order> =>
    api.patch(`/orders/${orderId}/cancel`, { reason }),

  getOrderRefunds: async (orderId: string): Promise<OrderRefund[]> => {
    logger.log('[API] 🔗 Fetching refunds for order:', orderId);
    const endpoint = `/refunds/customer/order/${orderId}`;
    logger.log('[API] 📡 Endpoint:', endpoint);
    const result = await api.get(endpoint);
    logger.log('[API] 📦 Raw API response:', result);
    return result;
  },

  getInvoice: async (orderId: string): Promise<string> => {
    const res = await api.get(`/orders/${orderId}/invoice`, { responseType: 'text' });
    return res.data || res;
  },
};

// ============================================================================
// WALLET
// ============================================================================

export interface WalletDepositDto {
  amount_cents: number;
  payment_method?: string;
  payment_intent_id?: string;
}

export const ecommerceWalletApi = {
  getBalance: (): Promise<Wallet> =>
    api.get('/wallet/balance'),

  deposit: (dto: WalletDepositDto): Promise<{ wallet: Wallet; transaction: WalletTransaction }> =>
    api.post('/wallet/deposit', dto),

  getTransactions: (params?: { limit?: number; offset?: number }): Promise<{ data: WalletTransaction[]; meta: any }> =>
    api.get('/wallet/transactions', { params }),
};

// ============================================================================
// BONS CADEAUX
// ============================================================================

export interface BonCadeauVerifie {
  code_formate: string;
  amount_cents: number;
  status: string;
  expires_at: string | null;
  utilisable: boolean;
  /** Phrase prête à afficher quand le bon ne passe pas (déjà utilisé, expiré…) */
  motif: string | null;
}

export const ecommerceGiftVouchersApi = {
  /** Lire le bon sans le consommer, pour confirmer le montant avant validation. */
  verifier: (code: string): Promise<BonCadeauVerifie> =>
    api.get(`/gift-vouchers/check/${encodeURIComponent(code)}`),

  consommer: (
    code: string,
  ): Promise<{ success: boolean; amount_euros: string; balance_euros: string }> =>
    api.post('/gift-vouchers/redeem', { code }),
};

// ============================================================================
// ADDRESSES
// ============================================================================

export interface CreateAddressDto {
  type: 'billing' | 'delivery';
  street: string;
  city: string;
  postal_code: string;
  country: string;
  is_default?: boolean;
  additional_info?: string;
}

export const ecommerceAddressesApi = {
  getAddresses: (): Promise<CustomerAddress[]> =>
    api.get('/customers/me/addresses'),

  createAddress: (dto: CreateAddressDto): Promise<CustomerAddress> =>
    api.post('/customers/me/addresses', dto),

  updateAddress: (
    addressId: string,
    data: Partial<CreateAddressDto>
  ): Promise<CustomerAddress> =>
    api.patch(`/customers/me/addresses/${addressId}`, data),

  deleteAddress: (addressId: string): Promise<void> =>
    api.delete(`/customers/me/addresses/${addressId}`),
};

// ============================================================================
// LIVE SHOPPING (Shop-Along)
// ============================================================================

export const ecommerceLiveShoppingApi = {
  getVendorAvailability: (): Promise<VendorAvailability[]> =>
    api.get('/live-shopping/vendors/availability'),

  createSession: (data: {
    scheduled_at?: string;
    vendor_id?: string;
  }): Promise<LiveSession> =>
    api.post('/live-shopping/sessions', data),

  getSession: (sessionId: string): Promise<LiveSession> =>
    api.get(`/live-shopping/sessions/${sessionId}`),

  endSession: (sessionId: string): Promise<LiveSession> =>
    api.patch(`/live-shopping/sessions/${sessionId}/end`),
};

// ============================================================================
// CART (Server-side persistence)
// ============================================================================

export const ecommerceCartApi = {
  clear: (): Promise<void> =>
    api.delete('/cart'),
};

// ============================================================================
// STRIPE
// ============================================================================

export const ecommerceStripeApi = {
  createCheckoutSession: async (data: { amount_cents: number; return_path?: string; draft_id?: string }): Promise<{ session_id: string; url: string }> => {
    return api.post('/stripe/create-checkout-session', data);
  },

  createOrderPayment: async (data: { order_id: string; amount_cents: number }): Promise<{ session_id: string; url: string }> => {
    return api.post('/stripe/create-order-payment', data);
  },
};

// ============================================================================
// PROMO CODES
// ============================================================================

export const ecommercePromoApi = {
  validate: async (code: string, orderTotalCents: number) => {
    return api.post('/orders/validate-promo', { code, order_total_cents: orderTotalCents });
  },
};

// Exporter tout dans un objet centralisé
// ============================================
// HORAIRES DE LA BOUTIQUE
// ============================================

export interface OrderingState {
  open: boolean;
  reason?: 'CLOSED_WINDOW' | 'CLOSURE';
  message?: string;
  next_open_at?: string;
  closes_at?: string;
  closure?: { from: string; to: string; reason?: string };
  /**
   * Catégories commandables MÊME hors fenêtre (épicerie), déjà étendues aux
   * sous-catégories par le serveur. Les précommandes le sont toujours.
   */
  always_open_category_ids?: string[];
  /** Fenêtre hebdomadaire du frais (heure belge), null si pas de fenêtre. */
  window?: import('../utils/fenetreCommande').FenetreHebdo | null;
}

/**
 * Ce que la boutique dit de sa livraison, sans révéler sa couverture : les
 * zones sont données par leur nom et leur tarif, jamais par leurs codes postaux.
 */
export interface ShopDeliveryConfig {
  enabled: boolean;
  /** 0 = dimanche … 6 = samedi. */
  days: number[];
  min_order_cents: number;
  instructions?: string | null;
  zones: Array<{ name: string; fee_cents: number; free_above_cents: number | null }>;
}

export interface PlageHoraire {
  /** 0 = dimanche … 6 = samedi */
  day: number;
  from: string;
  to: string | null;
  /** Lieu, quand il diffère du magasin (le dimanche, c'est le marché). */
  place?: string | null;
  /** Formulation libre quand l'heure de fin n'a pas de sens (« à partir de 7h30 »). */
  note?: string | null;
}

export interface ShopStoreInfo {
  name: string | null;
  address: string | null;
  phone: string | null;
  opening_hours: PlageHoraire[];
}

export interface ShopConfig {
  ordering: OrderingState;
  pickup: { days: number[]; slots: string[]; lead_days: number; max_days_ahead: number };
  delivery?: ShopDeliveryConfig;
  store?: ShopStoreInfo;
  /** Règles de précommande (pain, viande…) : dessinées en ligne du temps. */
  preorders?: import('../utils/ligneTempsPrecommande').TypePrecommande[];
  /** Congés du boulanger à venir (dates seulement, AAAA-MM-JJ). */
  baker_closures?: Array<{ start_date: string; end_date: string }>;
  /** Vidanges que le client peut annoncer au checkout (crédit FOLIES au retrait). */
  vidanges?: TypeVidange[];
}

export interface TypeVidange {
  id: string;
  name: string;
  price_cents: number;
  exemples: string[];
}

export interface PickupDates {
  dates: string[];
  slots: string[];
  constraints: Array<{ product_id: string; product_name: string; weekdays: number[] }>;
  /** Articles sans lesquels le panier partirait plus tôt (calculé par le serveur). */
  retards?: Array<{
    product_id: string;
    product_name: string;
    avec_lui: string | null;
    sans_lui: string;
    raison: string | null;
  }>;
}

export interface DeliveryQuote {
  deliverable: boolean;
  zone_name: string | null;
  fee_cents: number;
  free_applied: boolean;
  free_above_cents: number | null;
  min_order_cents: number;
  missing_for_minimum_cents: number;
  reason: string | null;
}

/** Ce que la page publique d'une commande peut montrer : son contenu, jamais la personne. */
export interface CommandePublique {
  order_number: string;
  created_at: string;
  status: string;
  livraison: boolean;
  date: string | null;
  prenom: string | null;
  payee: boolean;
  total_cents: number;
  lignes: Array<{ nom: string; quantite: number; unite: string | null; total_cents: number }>;
}

export const ecommerceShopApi = {
  /** La boutique prend-elle commande ? Et selon quels jours de retrait ? */
  getConfig: (): Promise<ShopConfig> => api.get('/shop-config'),

  /**
   * Dates de retrait proposables pour CE panier. Calculées par le serveur, qui
   * seul connaît les jours de fabrication du boulanger, ses congés et l'heure
   * limite — et qui appliquera exactement la même règle à la commande.
   */
  getPickupDates: (productIds: string[]): Promise<PickupDates> =>
    api.post('/shop-config/pickup-dates', { product_ids: productIds }),

  /** Commande ouverte par le QR du ticket client (`/shop/c/<jeton signé>`, 20/10/2026). */
  commandePublique: (jeton: string): Promise<CommandePublique> =>
    api.get(`/orders/public/${encodeURIComponent(jeton)}`),

  /** Fiche désignée par une adresse propre `/shop/p/<nom>-<ref>` (id null : retirée). */
  produitParAdresse: (segment: string): Promise<{ id: string | null; nom: string | null; chemin: string | null }> =>
    api.get(`/shop-config/produit-par-adresse/${encodeURIComponent(segment)}`),

  /**
   * Frais de livraison pour cette commune et ce panier. C'est la MÊME fonction
   * qui enregistrera le montant sur la commande : le prix annoncé ici est celui
   * qui sera facturé, et une adresse hors zone est refusée des deux côtés.
   */
  getDeliveryQuote: (postalCode: string, subtotalCents: number): Promise<DeliveryQuote> =>
    api.post('/shop-config/delivery-quote', {
      postal_code: postalCode,
      subtotal_cents: subtotalCents,
    }),
};

export const ecommerceApi = {
  shop: ecommerceShopApi,
  auth: customerAuthApi,
  products: ecommerceProductsApi,
  orders: ecommerceOrdersApi,
  wallet: ecommerceWalletApi,
  giftVouchers: ecommerceGiftVouchersApi,
  addresses: ecommerceAddressesApi,
  liveShop: ecommerceLiveShoppingApi,
  cart: ecommerceCartApi,
  stripe: ecommerceStripeApi,
  promo: ecommercePromoApi,
};
